/**
 * ForgotPasswordPage - página de pedido de recuperação de senha
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { api, getApiErrorMessage } from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setSubmitted(true);
            toast.success("Pedido enviado!");
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Erro ao enviar pedido."));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="min-h-[80vh] flex items-center justify-center py-10 px-4 bg-hero-radial-light"
            data-testid="forgot-password-page"
        >
            <Card className="w-full max-w-md p-7 rounded-2xl bg-card border-border">
                <div className="flex justify-center mb-5">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0B0B10] overflow-hidden">
                        <img src={LOGO_URL} alt="MeSafa" className="h-14 w-14 object-contain" />
                    </span>
                </div>

                {!submitted ? (
                    <>
                        <h1 className="font-display text-2xl font-semibold text-center">
                            Recuperar senha
                        </h1>
                        <p className="text-sm text-muted-foreground text-center mt-1">
                            Indique o seu email e enviaremos instruções para repor a senha.
                        </p>

                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div>
                                <Label htmlFor="forgot-email">Email</Label>
                                <Input
                                    id="forgot-email"
                                    type="email"
                                    placeholder="o-seu-email@exemplo.com"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="forgot-email-input"
                                />
                            </div>

                            <Button
                                type="submit"
                                disabled={loading || !email}
                                className="w-full btn-grad-purple text-white h-11"
                                data-testid="forgot-submit-button"
                            >
                                <Mail className="h-4 w-4 mr-2" />
                                {loading ? "A enviar…" : "Enviar pedido"}
                            </Button>
                        </form>
                    </>
                ) : (
                    <div className="text-center space-y-3" data-testid="forgot-success-state">
                        <div className="mx-auto h-14 w-14 rounded-full bg-[#2DD4BF]/15 grid place-items-center">
                            <CheckCircle2 className="h-7 w-7 text-[#2DD4BF]" />
                        </div>
                        <h2 className="font-display text-xl font-semibold">Pedido enviado!</h2>
                        <p className="text-sm text-muted-foreground">
                            Se o seu email estiver registado, irá receber instruções para repor a senha.
                            Verifique também a pasta de spam.
                        </p>
                        <p className="text-xs text-muted-foreground mt-4">
                            Caso não receba em 24h, contacte{" "}
                            <a
                                href="mailto:geral@mesafa.ao"
                                className="text-[#A78BFA] hover:underline"
                            >
                                geral@mesafa.ao
                            </a>
                            .
                        </p>
                    </div>
                )}

                <div className="mt-6 pt-4 border-t border-border text-center">
                    <Link
                        to="/entrar"
                        className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                        data-testid="forgot-back-to-login"
                    >
                        <ArrowLeft className="h-3 w-3" /> Voltar ao login
                    </Link>
                </div>
            </Card>
        </div>
    );
}
